/**
 * Хранение access-токена и прозрачное обновление сессии.
 *
 * Токен живёт в замыкании модуля, а не в localStorage: при XSS его нельзя
 * прочитать из хранилища, и живёт он 15 минут (docs/adr/0005-auth-strategy.md).
 * Refresh-токен коду недоступен вообще — он в HttpOnly-cookie, которую браузер
 * сам отправляет на /api/v1/auth/*.
 *
 * Плата за схему — перезагрузка страницы теряет токен: приложение на старте
 * вызывает refresh() и, если cookie жива, продолжает сессию.
 */

import { ApiError, apiFetch } from './client'

let accessToken: string | null = null

/** Один общий запрос обновления на все параллельные вызовы. */
let refreshInFlight: Promise<string> | null = null

type SessionListener = () => void
const sessionLostListeners = new Set<SessionListener>()

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/** Подписка на потерю сессии: UI переводит пользователя на страницу входа. */
export function onSessionLost(listener: SessionListener): () => void {
  sessionLostListeners.add(listener)
  return () => {
    sessionLostListeners.delete(listener)
  }
}

function notifySessionLost(): void {
  accessToken = null
  sessionLostListeners.forEach((listener) => {
    listener()
  })
}

interface RefreshResponse {
  access_token: string
  expires_in: number
}

/**
 * Обновить access-токен по refresh-cookie.
 *
 * Параллельные запросы, столкнувшиеся с истёкшим токеном, ждут один и тот же
 * промис: иначе каждый начал бы свою ротацию, а ротация отзывает предыдущий
 * refresh — второй запрос выглядел бы как переиспользование украденного токена
 * и оборвал бы все сессии пользователя.
 */
export function refreshSession(): Promise<string> {
  refreshInFlight ??= apiFetch<RefreshResponse>('/auth/refresh', { method: 'POST' })
    .then((body) => {
      accessToken = body.access_token
      return body.access_token
    })
    .catch((error: unknown) => {
      notifySessionLost()
      throw error
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

function withAuthorization(init: RequestInit | undefined, token: string): RequestInit {
  return {
    ...init,
    headers: { ...init?.headers, Authorization: `Bearer ${token}` },
  }
}

/**
 * Запрос от имени пользователя: подставляет токен и один раз обновляет сессию,
 * если сервер ответил 401.
 *
 * Повтор ровно один: если и после обновления пришёл 401, дело не в сроке
 * жизни токена, и второй круг только задержал бы ошибку.
 */
export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = accessToken ?? (await refreshSession())

  try {
    return await apiFetch<T>(path, withAuthorization(init, token))
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) {
      throw error
    }
    const renewed = await refreshSession()
    return apiFetch<T>(path, withAuthorization(init, renewed))
  }
}

/** Полностью забыть сессию — после выхода или неудачного обновления. */
export function clearSession(): void {
  accessToken = null
  refreshInFlight = null
}
